import * as React from 'react';
import { IPurchaseOrderLookupProps } from './PurchaseOrderLookup.properties';
import { getPurchaseOrderLookupColumns, getPurchaseOrderLookupSearchColumns, SEARCH_SCREEN_DIALOG_STYLE } from './constants';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import BasicLookupField from '@markinson/uicomponents-v2/BasicLookupField';
import { CustomTypesApi } from 'api/customType';
import { WithStyles, withStyles } from '@material-ui/core';
import styles from '../PurchaseOrderLookup/PurchaseOrderLookup.styles';
import { useSearchPurchaseOrdersLookup } from 'api/purchaseOrders/purchaseOrders';

const PurchaseOrderLookupBatchSize = 100;
const PurchaseOrderLookup = (props: Readonly<IPurchaseOrderLookupProps & WithStyles<typeof styles>>, ref: React.Ref<IBasicLookupFieldHandle>) => {
    const { onSelectedItemChange, onChange, fieldRef, classes, LookupLabel, placeholder, ...restProps } = props;
    const [currentSearchType, setCurrentSearchType] = React.useState<'Lookup' | 'Search'>();
    const usePurchaseOrderLookupMutation = useSearchPurchaseOrdersLookup();
    const [okButtonDisabled, setOkButtonDisabled] = React.useState(false);

    React.useEffect(
        () => {
            setCurrentSearchType('Lookup');
        },
        []
    );

    const retrievePurchaseOrders = React.useCallback(
        async (searchText, searchType, BatchPage?, BatchSize?, Sort?: string) =>
            usePurchaseOrderLookupMutation.mutateAsync
                ({
                    SearchText: searchText || '',
                    Type: searchType || 'Search',
                    CheckExactMatch: searchType === 'Lookup',
                    BatchPage,
                    BatchSize,
                    Sort
                }),
        []
    );

    const onSearch = React.useCallback(
        async (searchText: string | undefined, batchSize: number, batchPage: number, exactMatch?: boolean): Promise<any> => {
            if (exactMatch) {
                setCurrentSearchType('Lookup');
            } else {
                setCurrentSearchType('Search');
            }
            const data = await retrievePurchaseOrders(searchText, exactMatch ? 'Lookup' : 'Search', batchPage, batchSize)
                .then((response) => response?.PurchaseOrders || []);

            return data;
        },
        []
    );

    const onFindOption = React.useCallback(
        async (searchText: string | undefined): Promise<any> => {
            const data = await CustomTypesApi.lookupGet('PurchaseOrder')(searchText);

            return data;
        },
        []
    );

    const onModuleChange = React.useCallback(
        (moduleId) => {
            setCurrentSearchType(moduleId);
        },
        []
    );

    const handleOnSelectedItemChanged = (selectedItem, searchText) => {
        const pickSlipId = selectedItem?.Details?.PurchaseOrderId ? Number(selectedItem?.Details?.PurchaseOrderId) : selectedItem?.Code;

        if (onChange) {
            onChange(pickSlipId || '');
        }

        if (onSelectedItemChange) {
            onSelectedItemChange(selectedItem, searchText);
        }
    };

    const handleOnSelectionChange = (item: any) => {
        if (item) {
            if (item?.Details?.PurchaseOrderId === 0 && item?.Details?.PurchaseOrderGroup === '') {
                setOkButtonDisabled(true);
            } else {
                setOkButtonDisabled(false);
            }
        }
    };

    const onRowPrepared = (e): void => {
        if (e.rowType === 'data' && e?.data?.Details?.PurchaseOrderId === 0 && e?.data?.Details?.PurchaseOrderGroup === '') {
            e?.rowElement?.classList?.add(classes.inactiveRow);
        }
    };

    return (

        <BasicLookupField
            {...restProps}
            innerRef={fieldRef ? fieldRef : ref}
            valueField={'Code'}
            displayField={'Display'}
            label={LookupLabel ?? 'Purchase Orders'}
            descriptionField={'Label'}
            placeholder={placeholder ?? 'Purchase Orders'}
            onFindOption={onFindOption}
            batchSize={PurchaseOrderLookupBatchSize}
            lookupOverrides={
                {
                    columns: currentSearchType === 'Lookup' ? getPurchaseOrderLookupColumns() : getPurchaseOrderLookupSearchColumns(),
                    dialogStyle: SEARCH_SCREEN_DIALOG_STYLE,
                    hideColumnHeaders: false,
                    disableGridColumnLines: false
                }
            }
            onSearch={onSearch}
            column1Field={'FormattedValue'}
            column2Field={'Description'}
            isTabbedLookup={true}
            searchScreenTitle={currentSearchType === 'Lookup' ? 'Purchase Order Lookup' : 'Purchase Order Search'}
            onModuleChange={onModuleChange}
            shouldEnterBehaveAsTab={true}
            onSelectedItemChange={handleOnSelectedItemChanged}
            onSelectionChange={handleOnSelectionChange}
            isOkButtonDisabled={okButtonDisabled}
            dataGridOnRowPrepared={onRowPrepared}
            showSummaryPanel={false}
        />);
};

export default withStyles(styles, { index: 1 })(React.forwardRef(PurchaseOrderLookup));
